// Reports tab on a stored client scan (TYR-215). Both AI reports are generated
// and kept on the server against the stored audit bundle, so this tab only ever
// asks for them by lead id. Nothing here posts audit data.

import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Bot, Download, Loader2, RefreshCw } from 'lucide-react';
import { internalApi } from '../../lib/hubApi';
import { GapReportBody } from './GapReportBody';
import type { AIReport, CallBrief } from '../../types/report.types';

type Kind = 'brief' | 'client';

type SavedReport = {
  kind: Kind;
  report: Record<string, unknown>;
  model: string;
  generatedAt: number;
};

const KIND_LABEL: Record<Kind, string> = {
  brief: 'Pre-call brief (internal)',
  client: 'Client gap report',
};

const KIND_BLURB: Record<Kind, string> = {
  brief:
    'Top risks, upsell angles against TYRL services, questions to ask on the call, and the Agentforce read. Written for you, not the client.',
  client:
    'The same gap analysis the live consultant report produces: executive summary, critical issues, quick wins, growth opportunities.',
};

// Generation runs a Claude call, which the shared 30s axios timeout would cut off.
const GENERATE_TIMEOUT_MS = 180_000;

function timestamp(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorMessage(err: unknown): string {
  return axios.isAxiosError(err)
    ? ((err.response?.data as { error?: string } | undefined)?.error ?? err.message)
    : (err as Error).message;
}

export function ScanReportsTab({ leadId }: { leadId: string }) {
  const [reports, setReports] = useState<Partial<Record<Kind, SavedReport>>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Kind | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Kind, string>>>({});
  const [downloading, setDownloading] = useState<'pdf' | 'docx' | 'xlsx' | null>(null);

  useEffect(() => {
    let live = true;
    internalApi
      .get<{ reports: SavedReport[] }>(`/leads/${leadId}/reports`)
      .then((r) => {
        if (!live) return;
        const next: Partial<Record<Kind, SavedReport>> = {};
        for (const row of r.data.reports) next[row.kind] = row;
        setReports(next);
      })
      .catch((e) => {
        if (live) toast.error(`Could not load saved reports: ${errorMessage(e)}`);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [leadId]);

  const generate = async (kind: Kind) => {
    if (busy) return;
    setBusy(kind);
    setErrors((e) => ({ ...e, [kind]: undefined }));
    try {
      const r = await internalApi.post<{ report: SavedReport }>(
        `/leads/${leadId}/reports/${kind}`,
        null,
        { timeout: GENERATE_TIMEOUT_MS },
      );
      setReports((prev) => ({ ...prev, [kind]: r.data.report }));
      toast.success(`${KIND_LABEL[kind]} generated`);
    } catch (e) {
      const msg = errorMessage(e);
      setErrors((prev) => ({ ...prev, [kind]: msg }));
      toast.error(`Generation failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  };

  const download = async (format: 'pdf' | 'docx' | 'xlsx') => {
    if (downloading) return;
    setDownloading(format);
    try {
      const r = await internalApi.post(`/leads/${leadId}/export?format=${format}`, null, {
        responseType: 'blob',
        timeout: GENERATE_TIMEOUT_MS,
      });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      const match = (r.headers['content-disposition'] as string | undefined)?.match(
        /filename="([^"]+)"/,
      );
      a.href = url;
      a.download = match?.[1] ?? `OrgIQ_Org_Audit.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${errorMessage(e)}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 className="animate-spin" size={16} />
        Loading saved reports…
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-neutral-500 mr-auto">
          Download the full audit document, built from this stored scan and carrying the client
          report when one is saved. The Excel holds every row the scan captured.
        </span>
        {(['pdf', 'docx', 'xlsx'] as const).map((format) => (
          <button
            key={format}
            type="button"
            onClick={() => download(format)}
            disabled={downloading !== null}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-surface-2 border border-border text-neutral-400 hover:text-crux-light disabled:opacity-60 text-sm"
          >
            {downloading === format ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            {format === 'xlsx' ? 'Download Excel' : format.toUpperCase()}
          </button>
        ))}
      </div>

      {(['brief', 'client'] as const).map((kind) => (
        <section key={kind} className="border border-border bg-surface-1 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-crux-light">{KIND_LABEL[kind]}</h2>
              <p className="text-sm text-neutral-500 mt-1 max-w-2xl">{KIND_BLURB[kind]}</p>
              {reports[kind] ? (
                <p className="text-xs text-neutral-500 mt-2">
                  {reports[kind]!.model} · {timestamp(reports[kind]!.generatedAt)}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => generate(kind)}
              disabled={busy !== null}
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-surface-2 border border-border text-neutral-400 hover:text-crux-light disabled:opacity-60 text-sm shrink-0"
            >
              {busy === kind ? (
                <Loader2 size={14} className="animate-spin" />
              ) : reports[kind] ? (
                <RefreshCw size={14} />
              ) : (
                <Bot size={14} />
              )}
              {busy === kind ? 'Generating…' : reports[kind] ? 'Regenerate' : 'Generate'}
            </button>
          </div>

          {errors[kind] ? (
            <div className="mt-4 text-sm text-sunset bg-sunset/10 border border-sunset/30 px-4 py-3">
              {errors[kind]}
            </div>
          ) : null}

          {reports[kind] ? (
            <div className="mt-6 space-y-10">
              {kind === 'client' ? (
                <GapReportBody report={reports.client!.report as unknown as AIReport} />
              ) : (
                <BriefBody brief={reports.brief!.report as unknown as CallBrief} />
              )}
            </div>
          ) : busy === kind ? null : (
            <p className="mt-4 text-sm text-neutral-500">Not generated yet.</p>
          )}
        </section>
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">{children}</div>
  );
}

function BriefBody({ brief }: { brief: CallBrief }) {
  return (
    <>
      <section>
        <SectionLabel>Top risks</SectionLabel>
        <ol className="space-y-2">
          {brief.topRisks.map((risk, i) => (
            <li key={i} className="bg-surface-2 border border-border p-4 flex items-start gap-3">
              <span className="text-sm tabular-nums text-neutral-500 shrink-0">{i + 1}</span>
              <div>
                <div className="text-sm font-medium text-crux-light">{risk.title}</div>
                <p className="text-sm text-neutral-400 mt-1 leading-snug">{risk.why}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <SectionLabel>Upsell angles</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {brief.upsellAngles.map((angle, i) => (
            <div key={i} className="bg-surface-2 border border-border p-4">
              <div className="text-[11px] uppercase tracking-widest text-grow">{angle.service}</div>
              <p className="text-sm text-crux-light mt-1.5 leading-snug">{angle.angle}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Questions to ask on the call</SectionLabel>
        <ul className="space-y-1.5">
          {brief.callQuestions.map((q, i) => (
            <li key={i} className="text-sm text-crux-light flex gap-2">
              <span className="text-neutral-600 shrink-0">·</span>
              {q}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <SectionLabel>Agentforce readiness</SectionLabel>
        <p className="text-sm text-crux-light leading-relaxed">{brief.agentforceReadiness}</p>
      </section>
    </>
  );
}
